<template>
  <div class="pt-cell" :style="cellStyle">
    <div class="pt-cell__header">
      <span class="pt-cell__flag" v-if="flag">{{ flag }}</span>
      <span class="pt-cell__label">{{ label }}</span>
      <span v-if="showModelBadge && lastModel" class="pt-cell__model" :class="modelBadgeClass">{{ lastModel }}</span>
    </div>
    <div class="pt-cell__body" ref="body">
      <!-- 새 카드가 최상단에 등장, 기존 카드는 아래로 밀려난다.
           폰트 크기는 모든 카드 동일 (한 줄 글자 수 변경에 의한 레이아웃 출렁임 제거).
           시각적 구분은 카드 배경/글씨 투명도로만 표현. -->
      <transition-group
        v-if="reversedEntries && reversedEntries.length > 0"
        name="pt-entry"
        tag="div"
        class="pt-cell__entries"
      >
        <div
          v-for="(entry, idx) in reversedEntries"
          :key="entry.id"
          class="pt-cell__entry"
          :class="{
            'pt-cell__entry--current': isCurrentIdx(idx),
            'pt-cell__entry--failed': entry.failed,
          }"
          :style="entryStyle(idx)"
        >
          <p class="pt-cell__entry-text" :style="fontStyle">
            <template v-if="entry.failed">{{ $t ? $t('번역 실패') : '번역 실패' }}</template>
            <template v-else>{{ entry.text }}</template>
          </p>
        </div>
      </transition-group>
      <p v-else class="pt-cell__placeholder" :style="fontStyle">…</p>
    </div>
  </div>
</template>

<script>
const LANG_META = {
  ko: { label: '한국어',  flag: '🇰🇷' },
  en: { label: 'English', flag: '🇺🇸' },
  ja: { label: '日本語',  flag: '🇯🇵' },
  zh: { label: '中文',    flag: '🇨🇳' },
  vi: { label: 'Tiếng Việt', flag: '🇻🇳' },
  th: { label: 'ไทย',    flag: '🇹🇭' },
  id: { label: 'Bahasa Indonesia', flag: '🇮🇩' },
  uz: { label: 'O‘zbekcha', flag: '🇺🇿' },
  ne: { label: 'नेपाली',  flag: '🇳🇵' },
  mn: { label: 'Монгол', flag: '🇲🇳' },
  km: { label: 'ភាសាខ្មែរ', flag: '🇰🇭' },
  my: { label: 'မြန်မာစာ', flag: '🇲🇲' },
  ru: { label: 'Русский', flag: '🇷🇺' },
  es: { label: 'Español', flag: '🇪🇸' },
  fr: { label: 'Français', flag: '🇫🇷' },
  de: { label: 'Deutsch', flag: '🇩🇪' },
  ar: { label: 'العربية', flag: '🇸🇦' },
  hi: { label: 'हिन्दी', flag: '🇮🇳' },
  bn: { label: 'বাংলা', flag: '🇧🇩' },
  pt: { label: 'Português', flag: '🇧🇷' },
}

export default {
  name: 'PtCell',
  props: {
    /** 격자 설정 항목 — { lang, bg, fg, fontScale, label, flag } */
    config: {
      type: Object,
      required: true,
    },
    /** 컴포저블 cellsByLang[lang] — { entries: [...], lastModel, failed, ... } */
    runtime: {
      type: Object,
      default: function () { return { entries: [], lastModel: null, failed: false } },
    },
    /** 사용자 글로벌 폰트 스케일 */
    fontScaleGlobal: {
      type: Number,
      default: 1.0,
    },
    showModelBadge: {
      type: Boolean,
      default: false,
    },
    /** 'newest_top' = 새 카드가 최상단, 'newest_bottom' = 새 카드가 최하단 */
    direction: {
      type: String,
      default: 'newest_top',
    },
  },
  computed: {
    lang: function () {
      return this.config && this.config.lang ? this.config.lang : ''
    },
    label: function () {
      if (this.config && this.config.label) return this.config.label
      const meta = LANG_META[this.lang]
      return meta ? meta.label : this.lang
    },
    flag: function () {
      if (this.config && this.config.flag) return this.config.flag
      const meta = LANG_META[this.lang]
      return meta ? meta.flag : ''
    },
    entries: function () {
      return this.runtime && Array.isArray(this.runtime.entries) ? this.runtime.entries : []
    },
    reversedEntries: function () {
      // direction === 'newest_top' 이면 reverse (배열 끝 = 최신 → 상단으로).
      // 'newest_bottom' 이면 원본 그대로 (배열 끝 = 최신 = 하단).
      if (this.direction === 'newest_bottom') {
        return this.entries.slice()
      }
      return this.entries.slice().reverse()
    },
    /** 첫 표시 위치(idx 0) 가 최신인지 — 카드 강조용 */
    isNewestFirst: function () {
      return this.direction !== 'newest_bottom'
    },
    lastModel: function () {
      return this.runtime && this.runtime.lastModel ? this.runtime.lastModel : null
    },
    modelBadgeClass: function () {
      if (this.lastModel === 'flash-lite') return 'pt-cell__model--lite'
      if (this.lastModel === 'flash') return 'pt-cell__model--full'
      if (this.lastModel === 'passthrough') return 'pt-cell__model--pass'
      return 'pt-cell__model--fail'
    },
    cellStyle: function () {
      const bg = this.config && this.config.bg ? this.config.bg : '#0F172A'
      const fg = this.config && this.config.fg ? this.config.fg : '#FFFFFF'
      return {
        backgroundColor: bg,
        color: fg,
      }
    },
    scaledFontSize: function () {
      // placeholder/단일 텍스트용. 모든 entry 가 동일 크기로 보이도록 fontStyle 과 동일 factor 사용.
      return this.fontStyle.fontSize
    },
    fontStyle: function () {
      // 모든 카드가 동일한 폰트 크기 — 한 줄당 글자 수가 변하지 않아 레이아웃 안정.
      const cellScale = this.config && this.config.fontScale ? this.config.fontScale : 1.0
      const global = this.fontScaleGlobal ? this.fontScaleGlobal : 1.0
      const factor = cellScale * global * 0.85   // 사용자 피드백: 최신 글자 약간 더 작게
      const min = Math.round(16 * factor)
      const mid = (2.6 * factor).toFixed(2)
      const max = Math.round(48 * factor)
      return {
        fontSize: 'clamp(' + min + 'px, ' + mid + 'vw, ' + max + 'px)',
      }
    },
    entriesCount: function () {
      return this.entries.length
    },
    /** 마지막 entry 의 텍스트 — cell_partial 누적으로 길이 변할 때도 스크롤 보정용 */
    lastEntryText: function () {
      if (this.entries.length === 0) return ''
      const last = this.entries[this.entries.length - 1]
      return last && last.text ? last.text : ''
    },
  },
  watch: {
    entriesCount: function () {
      this._triggerAutoScroll()
    },
    lastEntryText: function () {
      this._triggerAutoScroll()
    },
    direction: function () {
      this.$nextTick(this._scrollToEdge)
    },
  },
  methods: {
    isCurrentIdx: function (idx) {
      // newest_top: idx 0 이 최신. newest_bottom: 마지막 idx 가 최신.
      if (this.isNewestFirst) return idx === 0
      return idx === this.reversedEntries.length - 1
    },
    /** 최신으로부터의 거리 (0 = 최신) — 페이드 강도 결정 */
    _distFromCurrent: function (idx) {
      if (this.isNewestFirst) return idx
      return (this.reversedEntries.length - 1) - idx
    },
    entryStyle: function (idx) {
      // 폰트 크기는 모든 카드 동일 (레이아웃 안정). 시각 구분은 글씨 투명도로만.
      // 최신: 1.0, 직전: 0.82, ... plateau 0.35.
      const dist = this._distFromCurrent(idx)
      const opacity = Math.max(0.35, 1.0 - dist * 0.18)
      return {
        opacity: opacity,
      }
    },
    _scrollToEdge: function () {
      const el = this.$refs.body
      if (!el) return
      // newest_top 이면 위로, newest_bottom 이면 아래로 스크롤.
      const top = this.isNewestFirst ? 0 : el.scrollHeight
      try {
        if (typeof el.scrollTo === 'function') {
          el.scrollTo({ top: top, behavior: 'smooth' })
        } else {
          el.scrollTop = top
        }
      } catch (e) {
        try { el.scrollTop = top } catch (e2) { /* ignore */ }
      }
    },
    /**
     * 카드 추가/텍스트 갱신 시 자동 스크롤 트리거.
     * transition-group 의 enter(0.45s) + move(0.5s) 애니메이션이 끝나기 전·후
     * 두 시점에 스크롤하여 새 카드가 화면 밖으로 잘리지 않도록 보장.
     */
    _triggerAutoScroll: function () {
      this.$nextTick(() => {
        // 1차: 즉시 보정 — 텍스트 길이 변화에 빠르게 반응
        setTimeout(this._scrollToEdge, 80)
        // 2차: 애니메이션 끝난 후 한 번 더 — 새 카드가 자리 잡은 최종 높이 기준
        setTimeout(this._scrollToEdge, 600)
      })
    },
  },
  mounted() {
    this.$nextTick(this._scrollToEdge)
  },
}
</script>

<style scoped>
.pt-cell {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.pt-cell__header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.08);
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.pt-cell__flag {
  font-size: 18px;
  line-height: 1;
}

.pt-cell__label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pt-cell__model {
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.15);
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.pt-cell__model--lite { background: rgba(56, 189, 248, 0.25); }
.pt-cell__model--full { background: rgba(168, 85, 247, 0.3); }
.pt-cell__model--pass { background: rgba(148, 163, 184, 0.25); }
.pt-cell__model--fail { background: rgba(239, 68, 68, 0.35); }

.pt-cell__body {
  /* 새 카드가 최상단에 등장 → 위에서부터 차곡차곡 누적.
     컨테이너 높이 초과 시 자동 스크롤로 최신이 항상 보임. */
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  padding: 10px 12px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scroll-behavior: smooth;
}

.pt-cell__entries {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
}

.pt-cell__body::-webkit-scrollbar { width: 6px; }
.pt-cell__body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}

/* ─── 카드 ──────────────────────────────────────────────────────
   각 entry 는 카드 박스로 명확히 구분. 폰트 크기는 모든 카드 동일하므로
   한 줄당 글자 수가 변하지 않아 레이아웃 출렁임 없음.
*/
.pt-cell__entry {
  margin: 0;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.04);
  /* 투명도가 변할 때 부드럽게 보간 (글씨 크기 변화는 없음) */
  transition: opacity 0.4s ease-out, background-color 0.4s ease-out;
}

.pt-cell__entry--current {
  /* 최신 카드는 격자 배경 위에 명확히 떠보이도록 더 불투명한 highlight */
  background: rgba(255, 255, 255, 0.20);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
}

.pt-cell__entry--failed {
  /* 실패는 italic + 약간 어두운 톤 */
}

.pt-cell__entry-text {
  margin: 0;
  text-align: center;
  font-weight: 600;
  line-height: 1.3;
  word-break: keep-all;
  overflow-wrap: anywhere;
  letter-spacing: -0.01em;
}

.pt-cell__entry--current .pt-cell__entry-text {
  font-weight: 700;
}

.pt-cell__entry--failed .pt-cell__entry-text {
  font-style: italic;
  font-weight: 500;
}

/* ─── transition-group 애니메이션 (Vue 2) ─────────────────────────
   "한 몸 처럼 슬라이드" 효과:
   - enter: 새 카드는 자기 자리에 빠르게 페이드인만 (transform 없음 → 겹침 제거)
   - move:  기존 카드들이 FLIP 으로 동일한 transition 곡선/시간으로 슬라이드 →
            여러 카드가 동시에 한 방향으로 일치하여 이동하므로 한 몸 효과.
   - leave: 한도 초과 시 빠른 페이드아웃.
*/
.pt-entry-enter-active {
  transition: opacity 0.25s ease-out;
}
.pt-entry-enter,
.pt-entry-enter-from {
  opacity: 0;
}
.pt-entry-enter-to {
  opacity: 1;
}

.pt-entry-move {
  transition: transform 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}

.pt-entry-leave-active {
  transition: opacity 0.25s ease-out;
  position: absolute;
  left: 12px;
  right: 12px;
}
.pt-entry-leave-to {
  opacity: 0;
}

.pt-cell__entry--current {
  font-weight: 800;
  margin-bottom: 0;
  /* 현재 발화는 살짝 강조 */
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.pt-cell__entry--failed {
  font-style: italic;
  font-weight: 500;
}

.pt-cell__placeholder {
  margin: 0;
  text-align: center;
  opacity: 0.25;
  font-weight: 500;
}
</style>
